import React, { useState } from 'react';
import { Trophy, Lock, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/AuthContext';

// Shown once, on the results screen, immediately after a session.
//
// The database enforces the "one chance" rule: the update policy only applies
// while publish_decided_at is null and the score is under an hour old. This
// component is the only place in the app that offers the choice.
export default function PublishScore({ scoreId }) {
  const { isAgent } = useAuth();
  const [decided, setDecided] = useState(null); // null | 'published' | 'private'
  const [saving, setSaving] = useState(false);

  // Nothing to publish (save failed, or the KB set was empty)
  if (!scoreId) return null;

  // Guests are not on the leaderboard at all, so there is no decision to make.
  if (!isAgent) return null;

  const decide = async (publish) => {
    setSaving(true);
    const { error } = await supabase
      .from('training_scores')
      .update({ is_published: publish, publish_decided_at: new Date().toISOString() })
      .eq('id', scoreId);

    setSaving(false);

    if (error) {
      toast.error("That didn't save", { description: error.message });
      return;
    }

    setDecided(publish ? 'published' : 'private');
    if (publish) toast.success('Added to the leaderboard.');
  };

  if (decided) {
    return (
      <div className="mb-8 mx-auto max-w-md flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
        {decided === 'published' ? (
          <>
            <Check className="w-4 h-4 text-primary flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              This result is on the leaderboard.
            </p>
          </>
        ) : (
          <>
            <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              Kept private. It still counts toward your badges and progress.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="mb-8 mx-auto max-w-md p-5 rounded-2xl border border-primary/20 bg-primary/5">
      <p className="text-sm font-semibold text-foreground mb-1">
        Put this on the leaderboard?
      </p>
      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
        Your choice now is final for this session. Either way it counts toward
        your badges, your progress, and the team topic averages.
      </p>

      <div className="flex gap-3">
        <Button
          onClick={() => decide(true)}
          disabled={saving}
          className="flex-1 rounded-full"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Trophy className="w-4 h-4 mr-2" />
          )}
          Add to leaderboard
        </Button>
        <Button
          variant="outline"
          onClick={() => decide(false)}
          disabled={saving}
          className="flex-1 rounded-full border-border"
        >
          <Lock className="w-4 h-4 mr-2" />
          Keep private
        </Button>
      </div>
    </div>
  );
}
